/**
 * React Query hooks for Team Chat API.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  Channel,
  ChannelDetail,
  ChannelCreatePayload,
  Message,
  MessageCreatePayload,
  MessageUpdatePayload,
  ChatGroup,
  ChatGroupCreatePayload,
  ChatGroupUpdatePayload,
  ChatGroupMemberActionPayload,
  DirectMessageConversation,
  DirectMessageCreatePayload,
  UnreadCount,
  MarkReadPayload,
  InviteUsersPayload,
  ChannelMembership,
  PaginatedResponse,
} from '../types/chat';

// Query keys
export const chatKeys = {
  all: ['chat'] as const,
  channels: () => [...chatKeys.all, 'channels'] as const,
  channel: (id: string) => [...chatKeys.channels(), id] as const,
  channelMessages: (channelId: string) =>
    [...chatKeys.channel(channelId), 'messages'] as const,
  channelMembers: (channelId: string) =>
    [...chatKeys.channel(channelId), 'members'] as const,
  channelSearch: (channelId: string, query: string) =>
    [...chatKeys.channel(channelId), 'search', query] as const,
  jobChannel: (jobId: string) => [...chatKeys.channels(), 'job', jobId] as const,
  groups: () => [...chatKeys.all, 'groups'] as const,
  group: (id: string) => [...chatKeys.groups(), id] as const,
  groupMessages: (groupId: string) =>
    [...chatKeys.group(groupId), 'messages'] as const,
  directMessages: () => [...chatKeys.all, 'dm'] as const,
  dmConversation: (userId: string) =>
    [...chatKeys.directMessages(), userId] as const,
  unread: () => [...chatKeys.all, 'unread'] as const,
  thread: (messageId: string) => [...chatKeys.all, 'thread', messageId] as const,
};

// Channels
export const useChannels = (type?: string) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);

  return useQuery({
    queryKey: chatKeys.channels(),
    queryFn: async () => {
      const response = await api.get<Channel[] | PaginatedResponse<Channel>>(
        `/api/teamchat/channels/?${params.toString()}`
      );
      // Handle both array and paginated response formats
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data.results || [];
    },
  });
};

export const useChannel = (channelId: string | null) => {
  return useQuery({
    queryKey: chatKeys.channel(channelId || ''),
    queryFn: async () => {
      if (!channelId) return null;
      const response = await api.get<ChannelDetail>(
        `/api/teamchat/channels/${channelId}/`
      );
      return response.data;
    },
    enabled: !!channelId,
  });
};

export const useCreateChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ChannelCreatePayload) => {
      const response = await api.post<ChannelDetail>(
        '/api/teamchat/channels/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
    },
  });
};

export const useUpdateChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      channelId,
      data,
    }: {
      channelId: string;
      data: Partial<ChannelCreatePayload>;
    }) => {
      const response = await api.patch<Channel>(
        `/api/teamchat/channels/${channelId}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { channelId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.channel(channelId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
    },
  });
};

export const useJoinChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (channelId: string) => {
      const response = await api.post<ChannelMembership>(
        `/api/teamchat/channels/${channelId}/join/`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, channelId) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.channel(channelId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
    },
  });
};

export const useLeaveChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (channelId: string) => {
      await api.post(`/api/teamchat/channels/${channelId}/leave/`, {});
    },
    onSuccess: (_, channelId) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.channel(channelId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
    },
  });
};

export const useHideChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (channelId: string) => {
      await api.post(`/api/teamchat/channels/${channelId}/hide/`, {});
    },
    onSuccess: () => {
      // Invalidate all chat lists to refresh the sidebar
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
      queryClient.invalidateQueries({ queryKey: chatKeys.directMessages() });
      queryClient.invalidateQueries({ queryKey: chatKeys.groups() });
    },
  });
};

export const useUnhideChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (channelId: string) => {
      await api.post(`/api/teamchat/channels/${channelId}/unhide/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
      queryClient.invalidateQueries({ queryKey: chatKeys.directMessages() });
      queryClient.invalidateQueries({ queryKey: chatKeys.groups() });
    },
  });
};

export const useInviteToChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      channelId,
      data,
    }: {
      channelId: string;
      data: InviteUsersPayload;
    }) => {
      const response = await api.post<{ added: ChannelMembership[] }>(
        `/api/teamchat/channels/${channelId}/invite/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { channelId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.channelMembers(channelId),
      });
      queryClient.invalidateQueries({ queryKey: chatKeys.channel(channelId) });
    },
  });
};

export const useChannelMembers = (channelId: string | null) => {
  return useQuery({
    queryKey: chatKeys.channelMembers(channelId || ''),
    queryFn: async () => {
      if (!channelId) return [];
      const response = await api.get<ChannelMembership[]>(
        `/api/teamchat/channels/${channelId}/members/`
      );
      return response.data;
    },
    enabled: !!channelId,
  });
};

export const useJobChannel = (jobId: string | null) => {
  return useQuery({
    queryKey: chatKeys.jobChannel(jobId || ''),
    queryFn: async () => {
      if (!jobId) return null;
      const response = await api.get<ChannelDetail>(
        `/api/teamchat/channels/for-job/${jobId}/`
      );
      return response.data;
    },
    enabled: !!jobId,
  });
};

// Cursor paginated response type
interface CursorPaginatedResponse<T> {
  next: string | null;
  previous: string | null;
  results: T[];
}

// Helper to extract path from full URL (cursor pagination returns absolute URLs)
const extractPathFromUrl = (url: string): string => {
  try {
    // If it's already a relative path, return as-is
    if (url.startsWith('/')) {
      return url;
    }
    // Parse absolute URL and extract path + search params
    const parsedUrl = new URL(url);
    return parsedUrl.pathname + parsedUrl.search;
  } catch {
    // If parsing fails, return original
    return url;
  }
};

// Messages with infinite scroll
export const useChannelMessages = (channelId: string | null) => {
  return useInfiniteQuery({
    queryKey: chatKeys.channelMessages(channelId || ''),
    queryFn: async ({ pageParam }) => {
      if (!channelId) return { results: [], next: null, previous: null };

      // If pageParam is provided, extract path from the absolute URL cursor
      const url = pageParam
        ? extractPathFromUrl(pageParam)
        : `/api/teamchat/channels/${channelId}/messages/`;
      const response = await api.get<CursorPaginatedResponse<Message>>(url);
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next,
    enabled: !!channelId,
    staleTime: 30000,
    select: (data) => {
      // Flatten all pages and reverse to show oldest first (newest at bottom)
      const allMessages = data.pages.flatMap((page) => page.results);
      return {
        ...data,
        messages: [...allMessages].reverse(),
      };
    },
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      channelId,
      data,
    }: {
      channelId: string;
      data: MessageCreatePayload;
    }) => {
      const response = await api.post<Message>(
        `/api/teamchat/channels/${channelId}/messages/`,
        data
      );
      return response.data;
    },
    onSuccess: (newMessage, { channelId }) => {
      // Add the message to the first page of the infinite query cache
      queryClient.setQueryData(
        chatKeys.channelMessages(channelId),
        (old: { pages: CursorPaginatedResponse<Message>[]; pageParams: (string | null)[] } | undefined) => {
          if (!old || !old.pages.length) {
            return {
              pages: [{ results: [newMessage], next: null, previous: null }],
              pageParams: [null],
            };
          }
          // Check if message already exists in first page
          if (old.pages[0].results.some((msg) => msg.id === newMessage.id)) {
            return old;
          }
          // Add to the beginning of first page (newest messages)
          return {
            ...old,
            pages: [
              { ...old.pages[0], results: [newMessage, ...old.pages[0].results] },
              ...old.pages.slice(1),
            ],
          };
        }
      );
    },
  });
};

export const useUpdateMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      channelId,
      messageId,
      data,
    }: {
      channelId: string;
      messageId: string;
      data: MessageUpdatePayload;
    }) => {
      const response = await api.patch<Message>(
        `/api/teamchat/channels/${channelId}/messages/${messageId}/`,
        data
      );
      return response.data;
    },
    onSuccess: (updatedMessage, { channelId }) => {
      queryClient.setQueryData(
        chatKeys.channelMessages(channelId),
        (old: { pages: Array<{ results: Message[]; next: string | null; previous: string | null }>; pageParams: (string | null)[] } | undefined) => {
          if (!old || !old.pages?.length) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((msg) =>
                msg.id === updatedMessage.id ? updatedMessage : msg
              ),
            })),
          };
        }
      );
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      channelId,
      messageId,
    }: {
      channelId: string;
      messageId: string;
    }) => {
      await api.delete(
        `/api/teamchat/channels/${channelId}/messages/${messageId}/`
      );
      return { channelId, messageId };
    },
    onSuccess: ({ channelId, messageId }) => {
      queryClient.setQueryData(
        chatKeys.channelMessages(channelId),
        (old: { pages: Array<{ results: Message[]; next: string | null; previous: string | null }>; pageParams: (string | null)[] } | undefined) => {
          if (!old || !old.pages?.length) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.filter((msg) => msg.id !== messageId),
            })),
          };
        }
      );
    },
  });
};

export const useThreadReplies = (
  channelId: string | null,
  messageId: string | null
) => {
  return useQuery({
    queryKey: chatKeys.thread(messageId || ''),
    queryFn: async () => {
      if (!channelId || !messageId) return [];
      const response = await api.get<Message[]>(
        `/api/teamchat/channels/${channelId}/messages/${messageId}/replies/`
      );
      return response.data;
    },
    enabled: !!channelId && !!messageId,
  });
};

// Fetch a single message by ID (for thread parent)
export const useMessage = (channelId: string | null, messageId: string | null) => {
  return useQuery({
    queryKey: [...chatKeys.channelMessages(channelId || ''), 'single', messageId],
    queryFn: async () => {
      if (!channelId || !messageId) return null;
      const response = await api.get<Message>(
        `/api/teamchat/channels/${channelId}/messages/${messageId}/`
      );
      return response.data;
    },
    enabled: !!channelId && !!messageId,
  });
};

// Search response type
interface SearchResponse {
  results: Message[];
  total: number;
  query: string;
}

// Search messages in channel
export const useSearchMessages = (
  channelId: string | null,
  query: string
) => {
  const trimmedQuery = query.trim();

  return useQuery({
    queryKey: chatKeys.channelSearch(channelId || '', trimmedQuery),
    queryFn: async () => {
      if (!channelId || !trimmedQuery) {
        return { results: [], total: 0, query: '' };
      }
      const response = await api.get<SearchResponse>(
        `/api/teamchat/channels/${channelId}/messages/search/?q=${encodeURIComponent(trimmedQuery)}`
      );
      return response.data;
    },
    enabled: !!channelId && trimmedQuery.length >= 2,
    staleTime: 30000,
  });
};

// Reactions
export const useAddReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      emoji,
    }: {
      messageId: string;
      emoji: string;
      channelId: string;
    }) => {
      const response = await api.post<{ id: string; emoji: string }>(
        `/api/teamchat/messages/${messageId}/reactions/`,
        { emoji }
      );
      return response.data;
    },
    onSuccess: (_, { channelId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.channelMessages(channelId),
      });
    },
  });
};

export const useRemoveReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      emoji,
    }: {
      messageId: string;
      emoji: string;
      channelId: string;
    }) => {
      await api.delete(
        `/api/teamchat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/`
      );
    },
    onSuccess: (_, { channelId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.channelMessages(channelId),
      });
    },
  });
};

// Direct Messages
export const useDirectMessages = () => {
  return useQuery({
    queryKey: chatKeys.directMessages(),
    queryFn: async () => {
      const response = await api.get<DirectMessageConversation[] | PaginatedResponse<DirectMessageConversation>>(
        '/api/teamchat/dm/'
      );
      // Handle both array and paginated response formats
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data.results || [];
    },
  });
};

export const useStartDM = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DirectMessageCreatePayload) => {
      const response = await api.post<ChannelDetail>('/api/teamchat/dm/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.directMessages() });
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
    },
  });
};

export const useDMMessages = (userId: string | null) => {
  return useQuery({
    queryKey: chatKeys.dmConversation(userId || ''),
    queryFn: async () => {
      if (!userId) return [];
      const response = await api.get<{ results: Message[] }>(
        `/api/teamchat/dm/${userId}/messages/`
      );
      return response.data.results;
    },
    enabled: !!userId,
  });
};

// Groups
export const useChatGroups = () => {
  return useQuery({
    queryKey: chatKeys.groups(),
    queryFn: async () => {
      const response = await api.get<ChatGroup[] | PaginatedResponse<ChatGroup>>('/api/teamchat/groups/');
      // Handle both array and paginated response formats
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data.results || [];
    },
  });
};

export const useChatGroup = (groupId: string | null) => {
  return useQuery({
    queryKey: chatKeys.group(groupId || ''),
    queryFn: async () => {
      if (!groupId) return null;
      const response = await api.get<ChatGroup>(
        `/api/teamchat/groups/${groupId}/`
      );
      return response.data;
    },
    enabled: !!groupId,
  });
};

export const useCreateChatGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ChatGroupCreatePayload) => {
      const response = await api.post<ChatGroup>('/api/teamchat/groups/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.groups() });
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
    },
  });
};

export const useUpdateChatGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      data,
    }: {
      groupId: string;
      data: ChatGroupUpdatePayload;
    }) => {
      const response = await api.patch<ChatGroup>(
        `/api/teamchat/groups/${groupId}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.group(groupId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.groups() });
    },
  });
};

export const useDeleteChatGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      await api.delete(`/api/teamchat/groups/${groupId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.groups() });
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
    },
  });
};

export const useAddGroupMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      data,
    }: {
      groupId: string;
      data: ChatGroupMemberActionPayload;
    }) => {
      const response = await api.post<{ added: string[] }>(
        `/api/teamchat/groups/${groupId}/add_members/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.group(groupId) });
    },
  });
};

export const useRemoveGroupMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      userId,
    }: {
      groupId: string;
      userId: string;
    }) => {
      await api.delete(`/api/teamchat/groups/${groupId}/members/${userId}/`);
    },
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.group(groupId) });
    },
  });
};

export const useGroupMessages = (groupId: string | null) => {
  return useQuery({
    queryKey: chatKeys.groupMessages(groupId || ''),
    queryFn: async () => {
      if (!groupId) return [];
      const response = await api.get<{ results: Message[] }>(
        `/api/teamchat/groups/${groupId}/messages/`
      );
      return response.data.results;
    },
    enabled: !!groupId,
  });
};

// Unread counts
export const useUnreadCounts = () => {
  return useQuery({
    queryKey: chatKeys.unread(),
    queryFn: async () => {
      const response = await api.get<UnreadCount[] | PaginatedResponse<UnreadCount>>('/api/teamchat/unread/');
      // Handle both array and paginated response formats
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data.results || [];
    },
    refetchInterval: 60000, // Refetch every minute
  });
};

export const useMarkChannelRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      channelId,
      data,
    }: {
      channelId: string;
      data?: MarkReadPayload;
    }) => {
      await api.post(
        `/api/teamchat/unread/channels/${channelId}/mark-read/`,
        data || {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.unread() });
      queryClient.invalidateQueries({ queryKey: chatKeys.channels() });
      queryClient.invalidateQueries({ queryKey: chatKeys.directMessages() });
    },
  });
};

// Total unread count helper
export const useTotalUnreadCount = () => {
  const { data: unreadCounts } = useUnreadCounts();
  return unreadCounts?.reduce((sum, item) => sum + item.unread_count, 0) || 0;
};

// Upload chat attachment
export const useUploadChatAttachment = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const response = await api.uploadChatAttachment(file);
      return response.data;
    },
  });
};
